"use client";

import { useMemo } from "react";
import { useParams } from "next/navigation";
import { ShellLink } from "@/src/components";
import { cn } from "utils";
import {
  AIInsightContentTabTypeEnum,
  AIInsightDocsContentTabProps,
} from "./types";
import { usePathname } from "@/src/hooks/usePathname";
import Translations from "@translate/components/AIInsight/AIInsightDocsContentTab";
import { I18nUtil, locales } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface TabItem {
  key: string;
  label: string;
  path: string;
}

export const AIInsightDocsContentTab = (
  props: AIInsightDocsContentTabProps,
) => {
  const {
    contentType = "news",
    className = "",
    category = "crypto",
    deviceType = "desktop",
  } = props;
  const pathname = usePathname(true);
  const params = useParams<{ locale?: string }>();

  // 从路径中提取 locale
  const locale = useMemo(() => {
    const segments = pathname.split("/").filter(Boolean);
    const firstSegment = segments[0] || "";
    if (locales.includes(firstSegment as any)) {
      return firstSegment;
    }
    return params.locale || "en";
  }, [pathname, params.locale]);

  // 构建路径前缀：英语不显示 locale，其他语言显示
  const pathPrefix = useMemo(() => {
    return locale === "en" ? "" : `/${locale}`;
  }, [locale]);

  const tabs: TabItem[] | undefined = useMemo(() => {
    const commonTabs = [
      {
        key: AIInsightContentTabTypeEnum.NEWS,
        label: I18n.t("Trending"),
        path: `${pathPrefix}/news/${category}`,
      },
    ];

    if (category === "crypto") {
      return [
        ...commonTabs,
        {
          key: AIInsightContentTabTypeEnum.BREAKING_NEWS,
          label: I18n.t("Breaking News"),
          path: `${pathPrefix}/news/${category}/breaking-news`,
        },
      ];
    }

    return commonTabs;
  }, [category, pathPrefix]);

  return (
    <div className={cn("bg-background_solid_secondary mx-auto", className)}>
      <div
        className={cn("flex gap-3 items-center", {
          "py-2": deviceType !== "mobile",
          "pt-4": deviceType === "mobile",
        })}
      >
        {tabs?.map((tab) => {
          const isActive = contentType === tab.key;
          return (
            <ShellLink
              title={tab.label}
              aria-label={tab.label}
              data-openreplay-label={`${`${tab.label} Link`} | ${tab.path}`}
              id="lnk-aiinsight-docs-content-tab"
              key={tab.key}
              href={tab.path}
              className={cn(
                "flex items-center justify-center min-w-[52px] rounded-[6px] transition-colors duration-200",
                isActive
                  ? "bg-background_brand_overlay10 border border-border_brand_transparent50 text-content_primary"
                  : "bg-background_white_overlay5 text-content_secondary",
                deviceType !== "mobile" ? "px-3 py-2" : "px-2 py-[6px]",
              )}
            >
              <p
                className={cn(
                  "whitespace-nowrap",
                  deviceType !== "mobile" ? "body_medium_m" : "button_s",
                )}
              >
                {tab.label}
              </p>
            </ShellLink>
          );
        })}
      </div>
    </div>
  );
};
